import { useSearchParams } from "react-router-dom";
import type { ProductSortOrder } from "@/lib/api-client";

const SORT_OPTIONS: Array<{ value: ProductSortOrder; label: string }> = [
  { value: "popularity", label: "Popularité" },
  { value: "rating", label: "Meilleures notes" },
  { value: "newest", label: "Nouveautés" },
  { value: "price_asc", label: "Prix croissant" },
  { value: "price_desc", label: "Prix décroissant" },
];

export interface SortBarProps {
  total: number;
  /** Affiche le déclencheur « Filtrer (N) » sous `lg` (VVS_MODELE_RESPONSIVE.md §6.5). */
  activeFilterCount?: number;
  onOpenFilters?: () => void;
}

/**
 * Porté depuis apps/web/components/product/SortBar.tsx. Seul changement :
 * `usePathname`/`useRouter` de next/navigation → `useSearchParams()` de
 * react-router-dom, dont le setter conserve déjà le pathname courant (pas
 * besoin de le reconstruire à la main).
 */
export function SortBar({ total, activeFilterCount = 0, onOpenFilters }: SortBarProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const current = (searchParams.get("sort") as ProductSortOrder | null) ?? "popularity";

  function handleChange(value: string) {
    const params = new URLSearchParams(searchParams);
    if (value === "popularity") params.delete("sort");
    else params.set("sort", value);
    setSearchParams(params);
  }

  return (
    <div className="flex items-center justify-between gap-3 border-b border-line bg-cream/95 py-3 backdrop-blur">
      <div className="flex items-center gap-3">
        {onOpenFilters ? (
          <button
            type="button"
            onClick={onOpenFilters}
            className="rounded-sm border border-navy px-3 py-2 font-body text-xs font-medium uppercase tracking-wide text-navy lg:hidden"
          >
            Filtrer{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        ) : null}
        <p className="font-body text-sm text-muted">
          {total} produit{total > 1 ? "s" : ""}
        </p>
      </div>

      <label className="flex items-center gap-2 font-body text-sm text-ink">
        <span className="hidden sm:inline">Trier par</span>
        <select
          value={current}
          onChange={(e) => handleChange(e.target.value)}
          className="rounded-sm border border-line bg-white px-2 py-2 text-sm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-navy"
          aria-label="Trier les produits"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
